import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { StorageDocumentLink } from '../../../components/storage-document-link/index';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';

type RecordFile = {
  doc?: { name?: string } | File;
  docLink?: string;
  id?: string;
};

export type RecordAttachedFilesPreviewProps = {
  docusignFileLinks?: string[];
  files?: RecordFile[];
  onDocumentClick?: (fileLink: string) => void;
  previewMode?: boolean;
};

export const RecordAttachedFilesPreview: FC<RecordAttachedFilesPreviewProps> = ({
  docusignFileLinks = [],
  files = [],
  onDocumentClick,
}) => {
  const docusignSet = new Set(docusignFileLinks);
  const hasDocusignSeparation = docusignFileLinks.length > 0;

  const manualFiles = files.filter(({ docLink }) => docLink && !docusignSet.has(docLink));
  const docusignLinks = docusignFileLinks;

  if (!hasDocusignSeparation) {
    return (
      <div className="flex min-w-0 max-w-[220px] flex-col gap-2 overflow-hidden">
        {files.length > 0 ? (
          files.map(({ doc, docLink, id }) =>
            docLink ? (
              <StorageDocumentLink
                key={docLink || id}
                label={doc?.name ?? getS3FileOriginalName(docLink) ?? docLink}
                link={docLink}
                localFile={doc instanceof File ? doc : undefined}
                onDocumentClick={onDocumentClick}
              />
            ) : null,
          )
        ) : (
          <span className="truncate text-xs font-[450] text-gray-700">-</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <div
        className={
          docusignLinks.length > 0
            ? 'flex w-full min-w-0 gap-3 border-b border-gray-100 py-2'
            : 'flex w-full min-w-0 gap-3 py-2'
        }
      >
        <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="ATTACHED_FILES_TITLE" />:
        </span>
        <div className="flex min-w-0 max-w-[220px] flex-col gap-2 overflow-hidden">
          {manualFiles.length > 0 ? (
            manualFiles.map(({ doc, docLink }) =>
              docLink ? (
                <StorageDocumentLink
                  key={docLink}
                  label={doc?.name ?? getS3FileOriginalName(docLink) ?? docLink}
                  link={docLink}
                  localFile={doc instanceof File ? doc : undefined}
                  onDocumentClick={onDocumentClick}
                />
              ) : null,
            )
          ) : (
            <span className="truncate text-xs font-[450] text-gray-700">-</span>
          )}
        </div>
      </div>
      {docusignLinks.length > 0 && (
        <div className="flex w-full min-w-0 gap-3 py-2">
          <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="DOCUSIGN_DOC" />:
          </span>
          <div className="flex min-w-0 max-w-[220px] flex-col gap-2 overflow-hidden">
            {docusignLinks.map((fileLink) => (
              <StorageDocumentLink
                key={fileLink}
                label={getS3FileOriginalName(fileLink) || fileLink}
                link={fileLink}
                onDocumentClick={onDocumentClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
